<script setup lang="ts">
import type { ApprovePriceOverrideErrors } from "@classes-service-types/approve-price-override-errors.ts";
import { PriceOverrideStatus } from "@classes-service-types/price-override-status.ts";
import { datadogLogs } from "@datadog/browser-logs";
import { useQueryClient } from "@tanstack/vue-query";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import Textarea from "primevue/textarea";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { computed, ref, toRefs } from "vue";
import { modalPresets } from "@/components/OperationsApp/components/Modals/styles.ts";
import { REJECTED_DISPLAY_TEXT } from "@/components/OperationsApp/constants.ts";
import { getPriceDifference } from "@/components/OperationsApp/utils.ts";
import { QUERY_KEY as LIST_QUERY_KEY } from "@/providers/priceOverrides/useGetPriceOverrides.ts";
import { QUERY_KEY as COUNTS_QUERY_KEY } from "@/providers/priceOverrides/useGetPriceOverridesCount.ts";
import { usePriceOverridesApprove } from "@/providers/priceOverrides/usePriceOverridesApprove.ts";
import { usePriceOverridesReject } from "@/providers/priceOverrides/usePriceOverridesReject.ts";
import { usePriceOverridesRevert } from "@/providers/priceOverrides/usePriceOverridesRevert.ts";
import { formattedPriceFromCents } from "@/util/formatter";
import type { Emits, Props } from "./types.ts";

defineOptions({
  name: "ApproveDeclineModal",
});

const props = withDefaults(defineProps<Props>(), {
  modalVisible: false,
});
const emit = defineEmits<Emits>();

const { data, updatedStatus, modalVisible } = toRefs(props);

const notes = ref("");
const toast = useToast();
const queryClient = useQueryClient();

const statusString = computed(() => {
  switch (updatedStatus?.value) {
    case PriceOverrideStatus.Approved:
      return PriceOverrideStatus.Approved;
    case PriceOverrideStatus.Reverted:
      return PriceOverrideStatus.Reverted;
    case PriceOverrideStatus.Rejected:
      return REJECTED_DISPLAY_TEXT;
    default:
      return "";
  }
});

const toggleModalVisibility = (value) => {
  if (!value) {
    emit("hide");
  }
};

const hideModal = () => {
  emit("hide");
};

const resetResults = async () => {
  await queryClient.refetchQueries({ queryKey: [LIST_QUERY_KEY], type: "all", exact: false });
  await queryClient.refetchQueries({ queryKey: [COUNTS_QUERY_KEY], type: "all", exact: false });
};

const { mutate: approve, isPending: isApproving } = usePriceOverridesApprove({
  onSuccess: async (data, variables) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      // TODO: 12/11/2025 Remove datadog logging after code stabilizes
      datadogLogs.logger.warn("Failed to approve price overrides for ids.", {
        errors: data.errors,
      });
      data.errors.forEach((error) => {
        toast.add({
          severity: "error",
          summary: "Price Override",
          detail: error.message,
          group: "br-adm",
          life: 5000,
        });
      });
    }
    toast.add({
      severity: "success",
      summary: "Price Override",
      detail: "Approve has succeeded.",
      group: "br-adm",
      life: 5000,
    });
    await resetResults();
    hideModal();
  },
  onError: (error, variables) => {
    // TODO: 12/11/2025 Remove datadog logging after code stabilizes
    datadogLogs.logger.warn("Failed calling approve price override.", {
      error: error.message,
      priceOverrideIds: variables?.payload?.ids ?? "unknown",
      decisionNotes: variables?.payload?.decision_notes ?? "unknown",
    });

    toast.add({
      severity: "error",
      summary: "Price Override",
      detail: "Approve has failed.",
      group: "br-adm",
      life: 5000,
    });
  },
  retry: 0,
});

const { mutate: decline, isPending: isDeclining } = usePriceOverridesReject({
  onSuccess: async (data, variables) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      // TODO: 12/11/2025 Remove datadog logging after code stabilizes
      datadogLogs.logger.warn("Failed to reject price overrides for ids.", {
        errors: data.errors,
      });

      data.errors.forEach((error) => {
        toast.add({
          severity: "error",
          summary: "Price Override",
          detail: error.message,
          group: "br-adm",
          life: 5000,
        });
      });
    }

    toast.add({
      severity: "success",
      summary: "Price Override",
      detail: "Decline has succeeded.",
      group: "br-adm",
      life: 5000,
    });
    await resetResults();
    hideModal();
  },
  onError: (error, variables) => {
    // TODO: 12/11/2025 Remove datadog logging after code stabilizes
    datadogLogs.logger.warn("Failed calling reject price override.", {
      error: error.message,
      priceOverrideIds: variables?.payload?.ids ?? "unknown",
      decisionNotes: variables?.payload?.decision_notes ?? "unknown",
    });
    toast.add({
      severity: "error",
      summary: "Price Override",
      detail: "Decline has failed.",
      group: "br-adm",
      life: 5000,
    });
  },
  retry: 0,
});

const { mutate: revert, isPending: isReverting } = usePriceOverridesRevert({
  onSuccess: async (data, variables) => {
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      // TODO: 12/11/2025 Remove datadog logging after code stabilizes
      datadogLogs.logger.warn("Failed to revert price override for ids.", {
        errors: data.errors,
      });
    }
    toast.add({
      severity: "success",
      summary: "Price Override",
      detail: "Revert has succeeded.",
      group: "br-adm",
      life: 5000,
    });
    await resetResults();
    hideModal();
  },
  onError: (error, variables) => {
    // TODO: 12/11/2025 Remove datadog logging after code stabilizes
    datadogLogs.logger.warn("Failed calling revert price override.", {
      error: error.message,
      priceOverrideId: variables?.payload ?? "unknown",
    });

    toast.add({
      severity: "error",
      summary: "Price Override",
      detail: "Revert has failed.",
      group: "br-adm",
      life: 5000,
    });
  },
  retry: 0,
});

const submit = () => {
  if (data?.value?.id) {
    const payload = { ids: [data.value.id], decision_notes: notes.value };
    if (updatedStatus?.value === PriceOverrideStatus.Approved) {
      approve({ payload });
    } else if (updatedStatus?.value === PriceOverrideStatus.Reverted) {
      const revertPayload = { id: data.value.id, decision_notes: notes.value };
      revert({
        payload: revertPayload,
        name: "",
      });
    } else {
      decline({ payload });
    }
  }
};
</script>

<template>
  <Toast position="bottom-right" group="br-adm" />
  <Dialog :visible="modalVisible" @update:visible="toggleModalVisibility" modal :dismissable-mask="true" :close-on-escape="true" :pt="modalPresets.dialog">
    <template #header>
      <div class="font-montserrat inline-flex align-items-center justify-content-center">
        <div>{{ statusString }} Details</div>
      </div>
    </template>
    <template #default>
      <div class="grid grid-cols-2 gap-x-4 gap-y-6">
        <div class="col-span-2">
          <header class="font-semibold mb-2">Student Name</header>
          <p>{{ data?.requested_by || "" }}</p>
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">Experience</header>
          <p>{{ data?.name || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Product Type</header>
          <p>{{ data?.product_type || "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Requested Amount</header>
          <p>{{ data?.override_amount_in_cents? formattedPriceFromCents(data?.override_amount_in_cents) : "" }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Difference Amount</header>
          <p>{{ formattedPriceFromCents(getPriceDifference(data)) }}</p>
        </div>
        <div>
          <header class="font-semibold mb-2">Original Amount</header>
          <p>{{ data?.original_amount_in_cents? formattedPriceFromCents(data?.original_amount_in_cents) : "" }}</p>
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">Reason</header>
          <p>{{ data?.override_reason || "" }}</p>
        </div>
        <div class="col-span-2">
          <header class="font-semibold mb-2">Notes</header>
          <Textarea class="w-full" v-model="notes" autoResize rows="5" maxlength="500" />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex ml-auto">
        <Button @click="hideModal" class="mr-2" outlined>Cancel</Button>
        <Button @click="submit" :loading="isApproving || isDeclining || isReverting">Submit</Button>
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
